import { useState, useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import api from 'api/api'

/**
 * Hook personalizado para manejar la carga de archivos
 * 
 * @param {Object} options - Opciones de configuración
 * @param {Number} options.maxFiles - Número máximo de archivos permitidos
 * @param {Number} options.maxFileSize - Tamaño máximo por archivo en bytes
 * @param {Array} options.allowedTypes - Tipos MIME o extensiones permitidas
 * @param {String} options.endpoint - Endpoint de la API para subir archivos
 * @param {String} options.fieldName - Nombre del campo en el FormData
 * @param {String} options.apiUrlPrefix - Prefijo para las URL de archivos existentes
 * @param {Boolean} options.uploadOnSelect - Si se deben subir los archivos al seleccionarlos
 * @param {Object|Boolean|null} options.maxFilesMessage - Mensaje a mostrar cuando se excede el límite de archivos
 *   Si es null o false, no se muestra nada
 *   Si es true, se muestra el mensaje por defecto como error
 *   Si es un objeto, debe tener las propiedades:
 *     - type: 'error', 'info', 'warning', 'success' (tipo de toast)
 *     - message: String (mensaje a mostrar)
 * @returns {Object} - Estado y funciones para manejar la carga de archivos
 */
export function useFileUpload(options = {}) {
	// Opciones con valores predeterminados
	const config = {
		maxFiles: 5,
		maxFileSize: 5000000, // 5MB
		allowedTypes: [],
		endpoint: 'a/multimedia',
		fieldName: 'foto',
		apiUrlPrefix: '',
		uploadOnSelect: false,
		maxFilesMessage: true, // Por defecto, mostrar mensaje de error
		...options,
	}

	// Estado para archivos
	const [selectedFiles, setSelectedFiles] = useState([])
	const [uploadedFiles, setUploadedFiles] = useState([])
	const [filesToDelete, setFilesToDelete] = useState([])
	const [isLoading, setIsLoading] = useState(false)
	const [pendingUploads, setPendingUploads] = useState([])

	// Limpiar URLs al desmontar el componente
	useEffect(() => {
		return () => {
			// Limpiar URLs de objetos al desmontar
			selectedFiles.forEach(file => {
				if (file.previewUrl) {
					URL.revokeObjectURL(file.previewUrl)
				}
			})
		}
	}, [selectedFiles])

	// Función para validar archivos
	const validateFiles = useCallback(
		(files) => {
			const validFiles = []
			const invalidFiles = []

			for (const file of files) {
				let isValid = true
				let errorReason = ''

				// Validar tamaño
				if (file.size > config.maxFileSize) {
					isValid = false
					errorReason = `El archivo excede el tamaño máximo (${config.maxFileSize / 1000000}MB)`
				}

				// Validar tipo si se especificaron tipos permitidos
				if (isValid && config.allowedTypes.length > 0) {
					const fileType = file.type
					const fileExtension = file.name.split('.').pop().toLowerCase()

					// Comprobar si el tipo MIME o la extensión están permitidos
					const isTypeAllowed = config.allowedTypes.some((type) => {
						// Si es un tipo MIME completo (ej: "image/jpeg")
						if (type.includes('/')) {
							return type === fileType || type === fileType.split('/')[0] + '/*'
						}
						// Si es una extensión (ej: ".jpg")
						else if (type.startsWith('.')) {
							return type.substring(1) === fileExtension
						}
						// Si es una categoría (ej: "image")
						else {
							return fileType.startsWith(type + '/')
						}
					})

					if (!isTypeAllowed) {
						isValid = false
						errorReason = `Tipo de archivo no permitido (${fileType})`
					}
				}

				if (isValid) {
					// Añadir URL de previsualización para imágenes y guardar referencia al archivo original
					if (file.type.startsWith('image/')) {
						const previewUrl = URL.createObjectURL(file)
						validFiles.push({
							// Propiedades básicas
							name: file.name,
							type: file.type,
							size: file.size,
							// Referencia al archivo original
							_file: file, // Store the original File object
							// URL de previsualización
							previewUrl,
						})
					} else {
						// Para archivos no imagen
						validFiles.push({
							name: file.name,
							type: file.type,
							size: file.size,
							_file: file, // Store the original File object
						})
					}
				} else {
					invalidFiles.push({
						file,
						error: errorReason,
					})
				}
			}

			// Mostrar errores si hay
			invalidFiles.forEach((item) => {
				toast.error(`Error en archivo ${item.file.name}: ${item.error}`)
			})

			return validFiles
		},
		[config.maxFileSize, config.allowedTypes]
	)

	// Función para mostrar mensaje de archivos excedidos según la configuración
	const showMaxFilesMessage = useCallback(() => {
		const { maxFilesMessage, maxFiles } = config

		if (maxFilesMessage === null || maxFilesMessage === false) {
			// No mostrar mensaje
			return
		}

		// Si es un objeto con type y message
		if (typeof maxFilesMessage === 'object') {
			const { type = 'error', message } = maxFilesMessage
			const actualMessage = message || `No se pueden seleccionar más de ${maxFiles} archivos`

			switch (type) {
				case 'info':
					toast.info(actualMessage)
					break
				case 'warning':
					toast.warning(actualMessage)
					break
				case 'success':
					toast.success(actualMessage)
					break
				case 'error':
				default:
					toast.error(actualMessage)
					break
			}
		} else {
			// Por defecto, mostrar como error
			toast.error(`No se pueden seleccionar más de ${maxFiles} archivos`)
		}
	}, [config.maxFilesMessage, config.maxFiles])

	// Función para manejar la selección de archivos
	const handleSelectFiles = useCallback(
		(files) => {
			if (!files || files.length === 0) return

			// Verificar si se excede el límite de archivos
			const totalFiles =
				selectedFiles.length + uploadedFiles.length - filesToDelete.length + files.length

			if (totalFiles > config.maxFiles) {
				// Mostrar mensaje según configuración
				showMaxFilesMessage()

				// Si se puede añadir algunos archivos, tomar solo los que caben
				const availableSlots =
					config.maxFiles - (selectedFiles.length + uploadedFiles.length - filesToDelete.length)

				if (availableSlots <= 0) return

				files = Array.from(files).slice(0, availableSlots)
			}

			// Validar los archivos
			const validFiles = validateFiles(files)

			// Filtrar duplicados
			const newFiles = validFiles.filter((newFile) => {
				// Verificar si ya existe en selectedFiles
				const isDuplicate = selectedFiles.some(
					(existingFile) =>
						existingFile.name === newFile.name && existingFile.size === newFile.size
				)

				return !isDuplicate
			})

			if (newFiles.length === 0) return

			// Actualizar estado
			setSelectedFiles((prev) => [...prev, ...newFiles])

			// Subir automáticamente si está configurado
			if (config.uploadOnSelect) {
				uploadFiles(newFiles)
			} else {
				// Agregar a pendingUploads para subir más tarde
				setPendingUploads((prev) => [...prev, ...newFiles])
			}
		},
		[
			selectedFiles,
			uploadedFiles,
			filesToDelete,
			config.maxFiles,
			validateFiles,
			showMaxFilesMessage,
			config.uploadOnSelect,
		]
	)

	// Función para subir archivos - CORREGIDA
	const uploadFiles = useCallback(
		async (filesToUpload = null) => {
			// Si no se especifican archivos, usar los pendientes
			const files = filesToUpload || pendingUploads
			if (files.length === 0) return []

			setIsLoading(true)
			const results = []

			try {
				for (const file of files) {
					const formData = new FormData()

					// Handle file based on its structure
					if (file instanceof File) {
						// Native File object
						formData.append(config.fieldName, file)
					} else if (file instanceof Blob) {
						// Blob object
						const fileObj = new File([file], file.name || 'archivo.dat', {
							type: file.type || 'application/octet-stream',
						})
						formData.append(config.fieldName, fileObj)
					} else if (file._file && (file._file instanceof File || file._file instanceof Blob)) {
						// Custom file object with valid _file
						formData.append(config.fieldName, file._file)
					} else {
						// Invalid file object
						results.push({
							success: false,
							error: 'Formato de archivo no válido',
							originalFile: file,
						})
						continue
					}

					try {
						const { datos } = await api.post(config.endpoint, formData)

						const result = {
							success: true,
							id: datos.id,
							url: datos.url,
							originalFile: file,
							nombre: file.name,
						}

						results.push(result)
					} catch (error) {
						console.error(`Error uploading file: ${file.name}`, error)
						results.push({
							success: false,
							error: error.message || 'Error al subir el archivo',
							originalFile: file,
						})
						toast.error(`Error al subir ${file.name}`)
					}
				}

				// Actualizar estado con los archivos subidos exitosamente
				const successfulUploads = results.filter((r) => r.success)
				if (successfulUploads.length > 0) {
					setUploadedFiles((prev) => [...prev, ...successfulUploads])

					// Eliminar los archivos subidos de la lista de seleccionados
					const uploadedFileNames = successfulUploads.map((r) => r.originalFile.name)
					setSelectedFiles((prev) =>
						prev.filter((file) => !uploadedFileNames.includes(file.name))
					)

					// Limpiar de pendingUploads
					setPendingUploads((prev) =>
						prev.filter((file) => !uploadedFileNames.includes(file.name))
					)
				}
			} finally {
				setIsLoading(false)
			}

			return results
		},
		[pendingUploads, config.endpoint, config.fieldName]
	)

	// Función para marcar archivos para eliminar
	const markFileForDeletion = useCallback((file) => {
		if (file.id) {
			// Si es un archivo ya subido con ID
			setFilesToDelete((prev) => [...prev, file.id])
		} else {
			// Si es un archivo seleccionado, solo eliminarlo
			setSelectedFiles((prev) => prev.filter((f) => f !== file))
			setPendingUploads((prev) => prev.filter((f) => f !== file))

			// Liberar URL
			if (file.previewUrl) {
				URL.revokeObjectURL(file.previewUrl)
			}
		}
	}, [])

	// Función para deshacer eliminación
	const undoFileDeletion = useCallback((file) => {
		if (file.id) {
			setFilesToDelete((prev) => {
				const newFilesToDelete = prev.filter((id) => id !== file.id)
				return newFilesToDelete
			})
		} else {
			console.log('useFileUpload: No ID found for file, cannot undo deletion', file)
		}
	}, [])

	// Inicializar con archivos existentes
	const initializeWithExistingFiles = useCallback(
		(existingFiles) => {
			if (!existingFiles || existingFiles.length === 0) return

			const formattedFiles = existingFiles.map((file) => {
				// Asegurarse de que cada archivo tenga las propiedades necesarias
				const formattedFile = { ...file }

				// Añadir prefijo a la URL si no lo tiene
				if (formattedFile.url && config.apiUrlPrefix && !formattedFile.url.startsWith('http')) {
					formattedFile.url = config.apiUrlPrefix + formattedFile.url
				}

				return formattedFile
			})

			setUploadedFiles(formattedFiles)
			setFilesToDelete([])
		},
		[config.apiUrlPrefix]
	)

	// Cálculo de la cantidad total de archivos
	const totalFiles = selectedFiles.length + uploadedFiles.length - filesToDelete.length

	// Obtener todos los archivos en orden
	const getAllFiles = useCallback(() => {
		const allUploadedFiles = uploadedFiles.filter((file) => !filesToDelete.includes(file.id))
		return [...allUploadedFiles, ...selectedFiles]
	}, [uploadedFiles, selectedFiles, filesToDelete])

	// Obtener IDs de archivos subidos (excluyendo los marcados para eliminar)
	const getUploadedFileIds = useCallback(() => {
		return uploadedFiles
			.filter((file) => file.id && !filesToDelete.includes(file.id))
			.map((file) => file.id)
	}, [uploadedFiles, filesToDelete])

	// Obtener IDs de archivos marcados para eliminar
	const getFilesToDeleteIds = useCallback(() => {
		return [...filesToDelete]
	}, [filesToDelete])

	return {
		// Estado
		selectedFiles,
		uploadedFiles,
		filesToDelete,
		isLoading,
		pendingUploads,
		totalFiles,
		remainingSlots: config.maxFiles - totalFiles,
		isFull: totalFiles >= config.maxFiles,

		// Funciones
		handleSelectFiles,
		uploadFiles,
		markFileForDeletion,
		undoFileDeletion,
		getAllFiles,
		getUploadedFileIds,
		getFilesToDeleteIds,
		initializeWithExistingFiles,

		// Función para resetear todo
		reset: () => {
			selectedFiles.forEach((file) => {
				if (file.previewUrl) {
					URL.revokeObjectURL(file.previewUrl)
				}
			})
			setSelectedFiles([])
			setUploadedFiles([])
			setFilesToDelete([])
			setPendingUploads([])
		}
	}
}